import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import CSVImportModal from '../components/CSVImportModal';
import PromoteStudentsModal from '../components/PromoteStudentsModal';
import { API_URL } from '../utils/api';
import studentsService from '../services/studentsService';
import classesService from '../services/classesService';
import campusService from '../services/campusService';
import { Plus, MagnifyingGlass, X, Upload, Download, FileCsv, CircleNotch, LockKey, ArrowUp } from '@phosphor-icons/react';

function StudentsPage() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [campuses, setCampuses] = useState([]);
  const [campusFilter, setCampusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const [credModal, setCredModal] = useState(null);
  const [credLoading, setCredLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: 'male',
    admission_date: new Date().toISOString().split('T')[0],
    nationality: 'Ghanaian',
    class_id: '',
    campus_id: '',
    admission_type: 'new',
    previous_school_name: '',
    transfer_certificate_number: ''
  });

  const loadStudents = useCallback(() => {
    setLoading(true);
    studentsService.listStudents({ search, campus_id: campusFilter || undefined })
      .then(data => setStudents(data.items || []))
      .catch(err => toast.error('Failed to load students'))
      .finally(() => setLoading(false));
  }, [search, campusFilter]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    const loadClasses = async () => {
      setClassesLoading(true);
      try {
        const data = await classesService.listClasses();
        // Handle different response structures
        const classList = data.items || data.data || data || [];
        setClasses(Array.isArray(classList) ? classList : []);
        if (!Array.isArray(classList) || classList.length === 0) {
          console.warn('No classes loaded from API. Response:', data);
        }
      } catch (err) {
        console.error('Failed to load classes:', err);
        toast.error('Failed to load classes');
      } finally {
        setClassesLoading(false);
      }
    };
    loadClasses();
  }, []);

  useEffect(() => {
    campusService.listCampuses()
      .then(data => setCampuses(Array.isArray(data) ? data : []))
      .catch(err => console.error('Failed to load campuses:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await studentsService.createStudent(formData);
      if (response?.waitlisted) {
        toast.info(`Student added — the selected class is full, so they were placed on the waitlist at position ${response.position}.`);
      } else {
        toast.success('Student added successfully!');
      }
      setShowModal(false);
      setFormData({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        gender: 'male',
        admission_date: new Date().toISOString().split('T')[0],
        nationality: 'Ghanaian',
        class_id: '',
        campus_id: '',
        admission_type: 'new',
        previous_school_name: '',
        transfer_certificate_number: ''
      });
      loadStudents();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleImport = async (file) => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a valid CSV file');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const result = await studentsService.importStudents(formData);

      if (result.success) {
        toast.success(result.message);
        if (result.errors && result.errors.length > 0) {
          // Show partial success with errors
          toast.warning(`Imported ${result.success_count} students with ${result.errors.length} errors. Check console for details.`);
          console.log('Import errors:', result.errors);
        }
      } else {
        toast.error(result.message);
        if (result.errors && result.errors.length > 0) {
          console.log('Import errors:', result.errors);
        }
      }

      setShowImportModal(false);
      loadStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const requiredColumns = [
    { name: 'student_id', description: 'Unique student identifier (e.g., STU001, STU002) - No duplicates allowed' },
    { name: 'first_name', description: "Student's first name" },
    { name: 'last_name', description: "Student's last name" },
    { name: 'date_of_birth', description: 'Birth date (Format: YYYY-MM-DD, e.g., 2005-03-15)' },
    { name: 'gender', description: "'male' or 'female' (case-insensitive)", values: ['male', 'female'] },
    { name: 'admission_date', description: 'Enrollment date (Format: YYYY-MM-DD)' }
  ];

  const optionalColumns = [
    { name: 'other_names', description: 'Middle or additional names' },
    { name: 'class', description: 'Class level (e.g., kg1, primary_1, jhs_1)' },
    { name: 'address', description: 'Residential address' },
    { name: 'nationality', description: "Country (defaults to 'Ghanaian' if empty)" },
    { name: 'religion', description: 'Religious affiliation' },
    { name: 'blood_group', description: 'Blood type (O+, O-, A+, A-, B+, B-, AB+, AB-)' },
    { name: 'medical_conditions', description: 'Medical notes/allergies' },
    { name: 'photo_url', description: 'URL to student photo' }
  ];

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = `${API_URL}/templates/students_import_template.csv`;
    link.download = 'students_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreatePortalCredentials = async (student) => {
    setCredLoading(true);
    try {
      const res = await studentsService.regenerateStudentCredentials(student.id);
      const creds = res.portal_account || res;
      setCredModal({ name: student.full_name, email: creds.email, password: creds.password });
    } catch (err) {
      toast.error(err?.detail || err?.response?.data?.detail || 'Failed to create portal credentials');
    } finally {
      setCredLoading(false);
    }
  };

  return (
    <Layout>
      <div className="page" data-testid="students-page">
        <div className="page-header">
          <div>
            <h1>Students</h1>
            <p>Manage student enrollment and records</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={() => setShowPromoteModal(true)} data-testid="promote-students-btn">
              <ArrowUp size={18} weight="bold" /> Promote Students
            </button>
            <button className="btn btn-secondary" onClick={() => setShowImportModal(true)} data-testid="import-students-btn">
              <Upload size={18} weight="bold" /> Import CSV
            </button>
            <button className="btn btn-primary" onClick={() => setShowModal(true)} data-testid="add-student-btn">
              <Plus size={18} weight="bold" /> Add Student
            </button>
          </div>
        </div>

        <div className="search-bar">
          <div className="search-input-wrapper">
            <MagnifyingGlass size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search students..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
            />
          </div>
          {campuses.length > 0 && (
            <select
              value={campusFilter}
              onChange={(e) => setCampusFilter(e.target.value)}
              data-testid="campus-filter"
              style={{ marginLeft: 8 }}
            >
              <option value="">All Campuses</option>
              {campuses.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <div className="loading">Loading students...</div>
        ) : (
          <div className="table-container">
            <table className="data-table" data-testid="students-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Name</th>
                  <th>Class</th>
                  <th>Gender</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => (
                  <tr key={student.id} data-testid={`student-row-${student.id}`}>
                    <td className="mono">{student.student_id}</td>
                    <td>{student.full_name}</td>
                    <td>{student.class_name || '-'}</td>
                    <td className="capitalize">{student.gender}</td>
                    <td>
                      <span className={`badge ${student.status}`}>{student.status}</span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm"
                        style={{ marginRight: '4px' }}
                        onClick={() => handleCreatePortalCredentials(student)}
                        disabled={credLoading}
                        title="Create portal account"
                      >
                        <LockKey size={14} weight="bold" />
                      </button>
                      <Link to={`/students/${student.id}`} className="btn btn-sm">View</Link>
                    </td>
                  </tr>
                ))}
                {students.length === 0 && (
                  <tr>
                    <td colSpan="6" className="empty-state">No students found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} data-testid="add-student-modal">
              <div className="modal-header">
                <h2>Add New Student</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      required
                      disabled={submitting}
                      data-testid="student-first-name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      required
                      disabled={submitting}
                      data-testid="student-last-name"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                      required
                      disabled={submitting}
                      data-testid="student-dob"
                    />
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData({...formData, gender: e.target.value})}
                      disabled={submitting}
                      data-testid="student-gender"
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Admission Date</label>
                    <input
                      type="date"
                      value={formData.admission_date}
                      onChange={(e) => setFormData({...formData, admission_date: e.target.value})}
                      required
                      disabled={submitting}
                      data-testid="student-admission-date"
                    />
                  </div>
                  <div className="form-group">
                    <label>Class</label>
                    <select
                      value={formData.class_id}
                      onChange={(e) => setFormData({...formData, class_id: e.target.value})}
                      disabled={classesLoading || submitting}
                      data-testid="student-class"
                    >
                      <option value="">
                        {classesLoading ? 'Loading classes...' : 'Select a class'}
                      </option>
                      {classes.map(cls => (
                        <option key={cls.id} value={cls.id}>
                          {cls.name}
                        </option>
                      ))}
                      {!classesLoading && classes.length === 0 && (
                        <option disabled>No classes available</option>
                      )}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Admission Type</label>
                    <select
                      value={formData.admission_type}
                      onChange={(e) => setFormData({...formData, admission_type: e.target.value})}
                      disabled={submitting}
                      data-testid="student-admission-type"
                    >
                      <option value="new">New admission</option>
                      <option value="transfer_in">Transfer in (from another school)</option>
                    </select>
                  </div>
                </div>
                {formData.admission_type === 'transfer_in' && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Previous School</label>
                      <input
                        type="text"
                        value={formData.previous_school_name}
                        onChange={(e) => setFormData({...formData, previous_school_name: e.target.value})}
                        disabled={submitting}
                        data-testid="student-previous-school"
                      />
                    </div>
                    <div className="form-group">
                      <label>Transfer Certificate No.</label>
                      <input
                        type="text"
                        value={formData.transfer_certificate_number}
                        onChange={(e) => setFormData({...formData, transfer_certificate_number: e.target.value})}
                        disabled={submitting}
                        data-testid="student-transfer-cert-number"
                      />
                    </div>
                  </div>
                )}
                {campuses.length > 0 && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Campus</label>
                      <select
                        value={formData.campus_id}
                        onChange={(e) => setFormData({...formData, campus_id: e.target.value})}
                        disabled={submitting}
                        data-testid="student-campus"
                      >
                        <option value="">No campus</option>
                        {campuses.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="save-student-btn">
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : (
                      'Save Student'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showImportModal && (
          <CSVImportModal
            isOpen={showImportModal}
            onClose={() => setShowImportModal(false)}
            title="Import Students from CSV"
            entityName="students"
            requiredColumns={requiredColumns}
            optionalColumns={optionalColumns}
            onImport={handleImport}
            onDownloadTemplate={downloadTemplate}
            importing={importing}
          />
        )}

        <PromoteStudentsModal
          open={showPromoteModal}
          onClose={() => setShowPromoteModal(false)}
          classes={classes}
          onDone={loadStudents}
        />
      </div>

      {credModal && (
        <div className="modal-overlay" onClick={() => setCredModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Portal Account Created</h2>
              <button className="modal-close" onClick={() => setCredModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-content">
              <p style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
                Share these credentials securely. The student must change their password on first login.
              </p>
              <div style={{ background: '#f0f4f0', borderRadius: 8, padding: '14px 18px', fontFamily: 'monospace', fontSize: 14, marginBottom: 16 }}>
                <div><strong>Name:</strong> {credModal.name}</div>
                <div style={{ marginTop: 8 }}><strong>Email:</strong> {credModal.email}</div>
                <div style={{ marginTop: 8 }}><strong>Password:</strong> <span style={{ background: '#d4e8d4', padding: '2px 8px', borderRadius: 4 }}>{credModal.password}</span></div>
              </div>
              <p style={{ fontSize: 12, color: '#888' }}>This password will not be shown again.</p>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={() => setCredModal(null)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default StudentsPage;
